<template>
  <main class="landing">
    <section class="hero">
      <div class="hero-content">
        <p class="eyebrow">AI Photo Restoration</p>
        <h1>Turn Black &amp; White Photos Into Color Memories</h1>
        <p class="hero-copy">
          Upload faded family photos and let AI rebuild realistic color tones in
          seconds. Perfect for restoration projects, albums, and storytelling.
        </p>
        <div class="hero-actions">
          <a
            v-if="isLoggedOut"
            class="cta-button primary"
            href="#"
            @click.prevent="openSignupModal"
          >Try Colorizer</a>
          <a class="cta-button subtle" href="#how-it-works">See How It Works</a>
        </div>
      </div>
    </section>

    <section id="how-it-works" class="section">
      <p class="eyebrow">Workflow</p>
      <h2>Simple Steps, Studio-Like Results</h2>
      <div class="grid three">
        <article class="card">
          <h3>1. Upload your B&amp;W image</h3>
          <p>Drop in JPG, PNG, or WEBP files from old scans or archives.</p>
        </article>
        <article class="card">
          <h3>2. AI analyzes scene details</h3>
          <p>Our model detects people, objects, lighting, and probable color cues.</p>
        </article>
        <article class="card">
          <h3>3. Download the colorized version</h3>
          <p>Preview and save your restored image for sharing or printing.</p>
        </article>
      </div>
    </section>

    <example-gallery />

    <section id="benefits" class="section">
      <p class="eyebrow">Why Use This Tool</p>
      <h2>Built For Restoring Old Photos</h2>
      <div class="grid two">
        <article class="card">
          <h3>Faster than manual editing</h3>
          <p>
            Skip hours in editing software and get high-quality first-pass
            colorization instantly.
          </p>
        </article>
        <article class="card">
          <h3>Natural-looking tones</h3>
          <p>
            The model aims for lifelike skin, clothing, sky, and environment colors
            while keeping the original composition.
          </p>
        </article>
        <article class="card">
          <h3>Great for family archives</h3>
          <p>
            Bring historical and personal photos to life before adding them to digital
            albums or framing.
          </p>
        </article>
        <article class="card">
          <h3>Works right in the browser</h3>
          <p>
            No desktop install needed. Log in, upload, and colorize from any device.
          </p>
        </article>
      </div>
    </section>

    <section id="faq" class="section faq-section">
      <p class="eyebrow">FAQ</p>
      <h2>Questions About Photo Colorization</h2>
      <div class="faq-list">
        <article class="faq-item">
          <h3>Why should I colorize black and white photos?</h3>
          <p>
            Colorization helps modern viewers connect with older photos by adding visual
            context, emotion, and realism that is often lost in grayscale.
          </p>
        </article>
        <article class="faq-item">
          <h3>Will this damage my original image?</h3>
          <p>
            No. The tool creates a new colorized output while your original black and
            white file stays unchanged.
          </p>
        </article>
        <article class="faq-item">
          <h3>How accurate are the colors?</h3>
          <p>
            AI predicts likely colors based on context. Results are usually realistic, and
            you can run different images for alternate interpretations.
          </p>
        </article>
        <article class="faq-item">
          <h3>What kinds of photos work best?</h3>
          <p>
            Clear, high-contrast portraits and scenes generally produce the most detailed
            and believable colorization.
          </p>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import ExampleGallery from '@/components/ExampleGallery.vue'

export default {
  name: 'LandingPage',
  components: {
    ExampleGallery,
  },
  inject: {
    openSignupModal: {
      from: 'openSignupModal',
      default() {
        return () => {}
      },
    },
  },
  props: {
    isLoggedOut: {
      type: Boolean,
      default: true,
    },
  },
}
</script>

<style scoped>
.landing {
  background: radial-gradient(circle at top right, rgba(108, 70, 236, 0.2), transparent 45%), #05060d;
  color: #f5f6ff;
  min-height: 100vh;
}

.hero {
  margin: 0;
  max-width: none;
  min-height: 560px;
  overflow: hidden;
  padding-top: 56px;
  padding-bottom: 56px;
  padding-left: max(env(safe-area-inset-left), clamp(16px, 5vw, 32px));
  padding-right: max(env(safe-area-inset-right), clamp(16px, 5vw, 32px));
  box-sizing: border-box;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero::before,
.hero::after {
  background-image: url("@/assets/background-01.jpg");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  bottom: 0;
  content: "";
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.hero::before {
  filter: grayscale(1) brightness(0.45);
  z-index: 1;
}

.hero::after {
  filter: brightness(0.75);
  mask-image: linear-gradient(135deg, transparent 0%, rgba(0, 0, 0, 1) 100%);
  z-index: 2;
}

.hero-content {
  margin: 0 auto;
  max-width: 580px;
  padding: 20px 0;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  text-align: left;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  min-height: 420px;
}

.eyebrow {
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  margin-bottom: 12px;
  text-transform: uppercase;
}

h1 {
  font-size: clamp(2rem, 4.2vw, 3.9rem);
  line-height: 1.05;
  margin: 0 0 18px;
}

.hero-copy {
  color: rgba(244, 245, 255, 0.85);
  font-size: 1.05rem;
  line-height: 1.6;
  margin-bottom: 26px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cta-button {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 28px;
  color: #f5f6ff;
  display: inline-flex;
  font-weight: 600;
  padding: 10px 18px;
  text-decoration: none;
}

.cta-button.primary {
  background: #b6f65a;
  border-color: #b6f65a;
  color: #0b0d17;
}

.section {
  margin: 0 auto;
  max-width: 1200px;
  padding: 54px 24px;
  text-align: left;
}

.section h2 {
  font-size: clamp(1.6rem, 3vw, 2.6rem);
  margin: 0 0 26px;
}

.grid {
  display: grid;
  gap: 14px;
}

.grid.three {
  grid-template-columns: 1fr;
}

.grid.two {
  grid-template-columns: 1fr;
}

.card {
  background: rgba(10, 12, 22, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 22px;
}

.card h3 {
  font-size: 1.12rem;
  margin: 0 0 10px;
}

.card p {
  color: rgba(245, 246, 255, 0.84);
  line-height: 1.6;
  margin: 0;
}

.faq-section {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-bottom: 72px;
}

.faq-list {
  display: grid;
  gap: 14px;
}

.faq-item {
  background: rgba(12, 14, 26, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 18px 20px;
}

.faq-item h3 {
  margin: 0 0 10px;
}

.faq-item p {
  color: rgba(245, 246, 255, 0.84);
  margin: 0;
}

@media (min-width: 860px) {
  .grid.three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .grid.two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
